import React, { useEffect, useMemo, useState } from "react";
import { StyleSheet } from "react-native";
import moment from "moment";
import { Calendar, LocaleConfig } from "react-native-calendars";
import { Box, Loading } from "../../components/common";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserExperience,
  getUserId,
} from "@microdoing/shared/services/user/selectors";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import { useScreenLoading } from "./hooks";
import theme from "app/Theme/Theme";
import ArrowRightSlider from "app/components/Icons/ArrowSlider/ArrowRightSlider";
import ArrowLeftSlider from "app/components/Icons/ArrowSlider/ArrowLeftSlider";
import { getStepFeedbacks } from "@microdoing/shared/services/userCampaign/selectors";
import { selectReviewFromFeedbackId } from "@microdoing/shared/services/userCampaign/actions";

LocaleConfig.locales["fr"] = {
  monthNames: [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ],
  monthNamesShort: [
    "Janv.",
    "Févr.",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juil.",
    "Août",
    "Sept.",
    "Oct.",
    "Nov.",
    "Déc.",
  ],
  dayNames: [
    "Dimanche",
    "Lundi",
    "Mardi",
    "Mercredi",
    "Jeudi",
    "Vendredi",
    "Samedi",
  ],
  dayNamesShort: ["Dim.", "Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam."],
  today: "Aujourd'hui",
};
LocaleConfig.defaultLocale = "fr";
LocaleConfig.locales.en = LocaleConfig.locales[""];

const styles = StyleSheet.create({
  main: {
    alignSelf: "center",
    width: "100%",
  },
  loader: {
    marginBottom: 10,
  },
});

const formatResults = results => {
  const goodFormat = {};
  results.forEach(step => {
    const date = moment(step.createdAt).format("YYYY-MM-DD");
    goodFormat[date] = {
      selected: true,
      stepId: step.stepId,
      _id: step._id,
      customStyles: {
        text: {
          ...theme.body["l3-semibold"],
        },
      },
    };
  });
  return goodFormat;
};

interface IProps {}

const HistoricalCalendar = ({}: IProps) => {
  const [isScreenLoading, setScreenLoading] = useScreenLoading();
  const [key, setKey] = useState("fr");
  const [loading, setLoading] = useState(false);

  const userId = useSelector(getUserId);
  const language = useSelector(getLanguage);
  const experience = useSelector(getUserExperience);
  const feedbacks = useSelector(getStepFeedbacks);

  const formatedFeedbacks = useMemo(() => {
    return formatResults(feedbacks);
  }, [feedbacks]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (language === "fr") {
      LocaleConfig.defaultLocale = "fr";
    } else {
      LocaleConfig.defaultLocale = "en";
    }
    setKey(language);
  }, [language]);

  const displayArrow = direction => {
    if (direction === "left") {
      return <ArrowLeftSlider color={theme.colors["brand.500"]} />;
    } else {
      return <ArrowRightSlider color={theme.colors["brand.500"]} />;
    }
  };

  if (isScreenLoading) return <Loading size={25} style={styles.loader} />;

  return (
    <Box paddingHorizontal="xm">
      <Calendar
        key={key}
        displayLoadingIndicator={loading}
        theme={{
          todayTextColor: theme.colors["brand.500"],
          dayTextColor: theme.colors["brand.500"],
          selectedDayBackgroundColor: theme.colors["brand.500"],
          selectedDayTextColor: theme.colors["neutral.white"],
        }}
        style={styles.main}
        hideExtraDays={true}
        markingType="custom"
        markedDates={formatedFeedbacks}
        // Date marking style [simple/period/multi-dot/custom]. Default = 'simple'
        // markingType={"period"}
        maxDate={Date()}
        // Initially visible month. Default = Date()
        // Minimum date that can be selected, dates before minDate will be grayed out. Default = undefined
        // Maximum date that can be selected, dates after maxDate will be grayed out. Default = undefined
        // Handler which gets executed on day press. Default = undefined
        onDayPress={day => {
          const activeDay = formatedFeedbacks[day.dateString];
          if (activeDay) {
            dispatch(selectReviewFromFeedbackId(activeDay._id));
          }
        }}
        // Handler which gets executed on day long press. Default = undefined
        onDayLongPress={day => {}}
        // Month format in calendar title. Formatting values: http://arshaw.com/xdate/#Formatting
        //   monthFormat={"yyyy MM"}
        // Handler which gets executed when visible month changes in calendar. Default = undefined
        onMonthChange={month => {}}
        renderArrow={displayArrow}
        // Hide month navigation arrows. Default = false
        // Replace default arrows with custom ones (direction can be 'left' or 'right')
        // Do not show days of other months in month page. Default = false
        // If hideArrows=false and hideExtraDays=false do not switch month when tapping on greyed out
        // day from another month that is visible in calendar page. Default = false
        // If firstDay=1 week starts from Monday. Note that dayNames and dayNamesShort should still start from Sunday.
        firstDay={1}
        // Hide day names. Default = false
        // Show week numbers to the left. Default = false
        // Handler which gets executed when press arrow icon left. It receive a callback can go back month
        onPressArrowLeft={subtractMonth => subtractMonth()}
        // Handler which gets executed when press arrow icon right. It receive a callback can go next month
        onPressArrowRight={addMonth => addMonth()}
        // Disable left arrow. Default = false
        // Disable right arrow. Default = false
        // Disable all touch events for disabled days. can be override with disableTouchEvent in markedDates
        disableAllTouchEventsForDisabledDays={true}
        // Replace default month and year title with custom one. the function receive a date as parameter.
        // Enable the option to swipe between months. Default = false
        enableSwipeMonths={false}
      />
    </Box>
  );
};

export default HistoricalCalendar;
